import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ActivityFeed = ({ activities }) => {
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  const filters = [
    { id: 'all', label: 'Todas', icon: 'List' },
    { id: 'matches', label: 'Peladas', icon: 'Calendar' },
    { id: 'barbecue', label: 'Churrascos', icon: 'Utensils' },
    { id: 'social', label: 'Social', icon: 'Users' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'match_created': return 'Plus';
      case 'match_joined': return 'UserPlus';
      case 'match_completed': return 'CheckCircle';
      case 'barbecue_planned': return 'Utensils';
      case 'friend_joined': return 'UserCheck';
      case 'achievement': return 'Award';
      case 'payment': return 'CreditCard';
      default: return 'Bell';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'match_created': return 'text-primary';
      case 'match_joined': return 'text-success';
      case 'match_completed': return 'text-success';
      case 'barbecue_planned': return 'text-secondary';
      case 'friend_joined': return 'text-accent';
      case 'achievement': return 'text-warning';
      case 'payment': return 'text-primary';
      default: return 'text-muted-foreground';
    }
  };

  const getActivityBg = (type) => {
    switch (type) {
      case 'match_created': return 'bg-primary/10';
      case 'match_joined': return 'bg-success/10';
      case 'match_completed': return 'bg-success/10';
      case 'barbecue_planned': return 'bg-secondary/10';
      case 'friend_joined': return 'bg-accent/10';
      case 'achievement': return 'bg-warning/10';
      case 'payment': return 'bg-primary/10';
      default: return 'bg-muted/50';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const time = new Date(timestamp);
    const diffInMinutes = Math.floor((now - time) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Agora';
    if (diffInMinutes < 60) return `${diffInMinutes}m atrás`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h atrás`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d atrás`;
    
    return time?.toLocaleDateString('pt-BR');
  };

  const handleActivityClick = (activity) => {
    switch (activity?.type) {
      case 'match_created': case'match_joined': case'match_completed': navigate('/match-details', { state: { matchId: activity?.matchId } });
        break;
      case 'barbecue_planned': navigate('/barbecue-planning', { state: { eventId: activity?.eventId } });
        break;
      case 'friend_joined': navigate('/user-profile', { state: { userId: activity?.userId } });
        break;
      default:
        break;
    }
  };

  const filteredActivities = activities?.filter(activity => {
    if (filter === 'all') return true;
    if (filter === 'matches') return ['match_created', 'match_joined', 'match_completed']?.includes(activity?.type);
    if (filter === 'barbecue') return activity?.type === 'barbecue_planned';
    if (filter === 'social') return ['friend_joined', 'achievement']?.includes(activity?.type);
    return true;
  });

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-heading font-bold text-lg text-card-foreground">
            Atividades Recentes
          </h2>
          <Button
            variant="ghost"
            size="sm"
            iconName="RefreshCw"
            iconSize={16}
            onClick={() => window.location?.reload()}
          >
            Atualizar
          </Button>
        </div>

        {/* Filter Tabs */}
        <div className="flex space-x-1 bg-muted rounded-lg p-1">
          {filters?.map((filterOption) => (
            <button
              key={filterOption?.id}
              onClick={() => setFilter(filterOption?.id)}
              className={`flex items-center space-x-2 px-3 py-2 text-sm font-medium rounded transition-standard ${
                filter === filterOption?.id
                  ? 'bg-card text-card-foreground shadow-elevation-1'
                  : 'text-muted-foreground hover:text-card-foreground'
              }`}
            >
              <Icon name={filterOption?.icon} size={14} />
              <span>{filterOption?.label}</span>
            </button>
          ))}
        </div>
      </div>
      {/* Activity List */}
      <div className="max-h-96 overflow-y-auto">
        {filteredActivities?.length > 0 ? (
          <div className="divide-y divide-border">
            {filteredActivities?.map((activity) => (
              <div
                key={activity?.id}
                onClick={() => handleActivityClick(activity)}
                className="p-4 hover:bg-muted/50 cursor-pointer transition-standard"
              >
                <div className="flex items-start space-x-3">
                  {/* Activity Icon */}
                  <div className={`p-2 rounded-lg ${getActivityBg(activity?.type)}`}>
                    <Icon 
                      name={getActivityIcon(activity?.type)} 
                      size={16} 
                      className={getActivityColor(activity?.type)} 
                    />
                  </div>

                  {/* Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="text-sm font-medium text-card-foreground">
                          {activity?.title}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">
                          {activity?.description}
                        </p>
                        
                        {/* Additional Info */}
                        {activity?.metadata && (
                          <div className="flex items-center space-x-4 mt-2">
                            {activity?.metadata?.venue && (
                              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                <Icon name="MapPin" size={12} />
                                <span>{activity?.metadata?.venue}</span>
                              </div>
                            )}
                            {activity?.metadata?.players && (
                              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                <Icon name="Users" size={12} />
                                <span>{activity?.metadata?.players} jogadores</span>
                              </div>
                            )}
                            {activity?.metadata?.amount && (
                              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                                <Icon name="CreditCard" size={12} />
                                <span>R$ {activity?.metadata?.amount?.toFixed(2)}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Timestamp */}
                      <div className="text-xs text-muted-foreground font-caption ml-2">
                        {formatTimeAgo(activity?.timestamp)}
                      </div>
                    </div>

                    {/* Participants */}
                    {activity?.participants && activity?.participants?.length > 0 && (
                      <div className="flex items-center space-x-2 mt-3">
                        <div className="flex -space-x-2">
                          {activity?.participants?.slice(0, 3)?.map((participant, index) => (
                            <div key={index} className="w-6 h-6 bg-primary rounded-full border-2 border-card flex items-center justify-center">
                              {participant?.avatar ? (
                                <Image 
                                  src={participant?.avatar} 
                                  alt={participant?.avatarAlt}
                                  className="w-6 h-6 rounded-full object-cover"
                                />
                              ) : (
                                <Icon name="User" size={12} color="white" />
                              )}
                            </div>
                          ))}
                          {activity?.participants?.length > 3 && (
                            <div className="w-6 h-6 bg-muted rounded-full border-2 border-card flex items-center justify-center">
                              <span className="text-xs text-muted-foreground">
                                +{activity?.participants?.length - 3}
                              </span>
                            </div>
                          )}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          {activity?.participants?.length > 1 
                            ? `${activity?.participants?.[0]?.name} e mais ${activity?.participants?.length - 1}`
                            : activity?.participants?.[0]?.name
                          }
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="p-8 text-center">
            <div className="flex items-center justify-center w-12 h-12 bg-muted/50 rounded-lg mx-auto mb-3">
              <Icon name="Activity" size={20} className="text-muted-foreground" />
            </div>
            <p className="text-sm text-muted-foreground">
              Nenhuma atividade encontrada
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              {filter === 'all' ?'Participe de peladas para ver atividades aqui' :'Tente alterar o filtro para ver mais atividades'
              }
            </p>
          </div>
        )}
      </div>
      {/* Footer */}
      {filteredActivities?.length > 0 && (
        <div className="p-4 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            fullWidth
            className="text-primary hover:text-primary"
          >
            Ver todas as atividades
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;